import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/material/styles';
import { useMutation } from 'react-query';
import { useImagePreviewNavigation } from '../../../shared/imageNavigation/state/use-image-preview-navigation';
import { usePrefetchAdjacentImages } from '../state/use-prefetch-adjacent-images';
import { useImageDownloadButton } from '../state/use-image-download-button';
import { getImageGalleryPosition } from '../state/image-gallery-position';
import SwipeListener from '../../../../../../../shared/SwipeListener';
import { Translate } from '../../../../../../../shared/LanguageService';
import { CategoryExtra } from './CategoryExtra';
import { EditableFilename } from './EditableFilename';
import { ImagePreviewNavigation } from '../../../shared/imageNavigation/view/ImagePreviewNavigation';
import * as Features from '../../../../../../../shared/Features';
import * as PhotoStatus from '../../../../../../../shared/PhotoStatus';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { ImageCategory } from '../../shared/state/utils';
import { useTelemetryContext } from '../../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../../telemetry/utils';
import { PreviewImageDisplay } from './PreviewImageDisplay';

type ImagePreviewDialogProps = {
    open: boolean;
    onClose: () => void;
    src: string;
    filename: string;
    category: number;
    idList: number[];
    currentImageId: number;
    onNavigate: (newImageId: number) => void;
    canEditFilename?: boolean;
    onFilenameSave?: (newFilename: string) => Promise<any>;
    onPrefetch?: (imageId: number) => void;
    photoStatus: number;
    onDownloadImage: (imageId: number) => Promise<void>;
};

type DataLayerProps = {
    user: AuthenticatedUser;
    open: boolean;
    onClose: () => void;
    src: string;
    filename: string;
    category: number;
    idList: number[];
    currentImageId: number;
    orderId: number;
    onNavigate: (newImageId: number) => void;
    photoStatus: number;
    onFilenameSave?: (newFilename: string) => void;
    onPrefetch?: (imageId: number) => void;
};

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        width: 'calc(100vw - 64px)',
        height: 'calc(100vh - 64px)',
        maxWidth: 'none',
        maxHeight: 'none',
        margin: '32px',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#dde3e3',
        display: 'flex',
        flexDirection: 'column'
    }
});

const Header = styled('header')({
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 16px',
    backgroundColor: '#dde3e3',
    color: '#333',
    minHeight: '48px'
});

const HeaderHeading = styled('h2')({
    margin: 0,
    fontSize: '14px',
    fontWeight: 400,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
});

const Spacer = styled('div')({
    display: 'flex',
    flex: '1 auto'
});

const NavigationWrapper = styled('div')({
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
});

function ImagePreviewDialog({
    open,
    onClose,
    src,
    filename,
    category,
    idList,
    currentImageId,
    onNavigate,
    canEditFilename = false,
    onFilenameSave,
    onPrefetch,
    photoStatus,
    onDownloadImage
}: ImagePreviewDialogProps) {
    var theme = useTheme();
    var isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { onSwipeLeft, onSwipeRight, canGoLeft, canGoRight } = useImagePreviewNavigation({
        idList,
        currentImageId,
        onNavigate,
        enabled: open
    });

    const { showNavigationArrows, imageCounterLabel, previousImageId, nextImageId } =
        getImageGalleryPosition({ idList, currentImageId, canGoLeft, canGoRight });

    usePrefetchAdjacentImages({ open, onPrefetch, previousImageId, nextImageId });

    const { isDownloading, handleDownload } = useImageDownloadButton({
        onDownloadImage,
        currentImageId
    });

    return (
        <StyledDialog open={open} onClose={onClose} aria-labelledby="image-preview-title">
            <Header>
                <HeaderHeading>
                    {Translate('image-processing/modal/title/image-showcase')}:
                </HeaderHeading>
                <EditableFilename
                    filename={filename}
                    canEdit={canEditFilename && !isMobile}
                    onSave={
                        onFilenameSave ||
                        function () {
                            return Promise.resolve();
                        }
                    }
                />
                {showNavigationArrows ? (
                    <ImagePreviewNavigation
                        onPrevious={onSwipeLeft}
                        onNext={onSwipeRight}
                        canGoPrevious={canGoLeft}
                        canGoNext={canGoRight}
                        counterLabel={imageCounterLabel}
                    />
                ) : null}
                {category === ImageCategory.Extra ? <CategoryExtra /> : null}
                <Spacer />
                <IconButton
                    aria-label="Close preview"
                    onClick={onClose}
                    sx={{ color: '#333' }}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </Header>
            <DialogContent
                sx={{
                    display: 'flex',
                    flex: 1,
                    padding: 0,
                    overflow: 'hidden',
                    '&.MuiDialogContent-root': { padding: 0 }
                }}
            >
                <NavigationWrapper>
                    <SwipeListener
                        onSwipeLeft={onSwipeLeft}
                        onSwipeRight={onSwipeRight}
                        style={{ display: 'flex', flex: 1 }}
                    >
                        <PreviewImageDisplay key={currentImageId} src={src} alt={filename} />
                    </SwipeListener>
                </NavigationWrapper>
            </DialogContent>
            {photoStatus >= PhotoStatus.CustomerApproved && category === ImageCategory.Normal ? (
                <DialogActions>
                    <Button
                        variant="contained"
                        startIcon={
                            isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />
                        }
                        onClick={handleDownload}
                        disabled={isDownloading}
                    >
                        {Translate('order/image-processing/button-toolbar/button/download')}
                    </Button>
                </DialogActions>
            ) : null}
        </StyledDialog>
    );
}

export default function DataLayer({
    user,
    orderId,
    currentImageId,
    onFilenameSave: onFilenameSaveNotify,
    open,
    onClose,
    src,
    filename,
    category,
    idList,
    onNavigate,
    photoStatus,
    onPrefetch
}: DataLayerProps) {
    let canEditFilename =
        Features.hasFeature(Features.UpdateFilename, user) && category !== ImageCategory.Extra;
    let { logger } = useTelemetryContext();

    let mutation = useMutation((newFilename: string) =>
        ImageProcessingApi.updateFilename(orderId, currentImageId, newFilename)
    );

    function handleDownloadImage(imageId: number): Promise<void> {
        return ImageProcessingApi.downloadImage(orderId, imageId).catch((error) => {
            let additionalInfo = {
                origin: 'ImagePreviewDialog.handleDownloadImage',
                userInfo: getUserInfo(user)
            };

            logger.logException(error, additionalInfo);
        });
    }

    function handleSaveFilename(newFilename) {
        if (onFilenameSaveNotify) {
            onFilenameSaveNotify(newFilename);
        }
        return mutation.mutateAsync(newFilename);
    }

    return (
        <ImagePreviewDialog
            open={open}
            onClose={onClose}
            src={src}
            filename={filename}
            category={category}
            idList={idList}
            currentImageId={currentImageId}
            onNavigate={onNavigate}
            photoStatus={photoStatus}
            onPrefetch={onPrefetch}
            canEditFilename={canEditFilename}
            onFilenameSave={handleSaveFilename}
            onDownloadImage={handleDownloadImage}
        />
    );
}
